
import React, { useState, useMemo } from 'react';
import { Search, Book, Monitor, Wifi, Printer, ChevronRight, FileText } from 'lucide-react';
import { Button } from '../shared';

// Mock Data for KB Articles
const ARTICLES = [
    {
        id: '1',
        title: 'Reiniciar POS Verifone',
        category: 'POS',
        content: 'Para reiniciar el POS Verifone:\n1. Mantén presionado el botón rojo y el botón amarillo simultáneamente por 5 segundos.\n2. El equipo se apagará.\n3. Presiona el botón verde para encenderlo nuevamente.',
        views: 1240
    },
    {
        id: '2',
        title: 'Sin conexión a Internet en Tienda',
        category: 'Conectividad',
        content: 'Pasos básicos de diagnóstico:\n1. Verifica que el router tenga luces encendidas.\n2. Revisa si otros dispositivos conectan.\n3. Desconecta el cable de corriente del router, espera 10 segundos y vuelve a conectar.\n4. Si el problema persiste, reporta el ticket.',
        views: 856
    },
    {
        id: '3',
        title: 'Impresora de Boletas no imprime',
        category: 'Periféricos',
        content: '1. Revisa que tenga papel y esté bien colocado.\n2. Verifica que la tapa esté completamente cerrada.\n3. Comprueba que el cable USB esté conectado firmemente.\n4. Apaga y enciende la impresora.',
        views: 632
    },
    {
        id: '4',
        title: 'Cambio de Rollo de Papel',
        category: 'Periféricos',
        content: 'Levanta la tapa azul, retira el cono vacío. Coloca el nuevo rollo con el papel saliendo por abajo hacia el frente. Cierra la tapa hasta escuchar un click.',
        views: 420
    },
    {
        id: '5',
        title: 'Error de VPN al conectar',
        category: 'Software',
        content: 'Si recibes error al conectar VPN Forticlient: Ir a Configuración > General y asegurar que la dirección del gateway sea la correcta. Contactar a Mesa Central si persiste.',
        views: 215
    }
];

const CATEGORIES = [
    { id: 'POS', name: 'Terminales POS', icon: Monitor, color: 'bg-blue-100 text-blue-600' },
    { id: 'Conectividad', name: 'Internet y Red', icon: Wifi, color: 'bg-green-100 text-green-600' },
    { id: 'Periféricos', name: 'Impresoras / Lectores', icon: Printer, color: 'bg-purple-100 text-purple-600' },
    { id: 'Software', name: 'Sistemas y Apps', icon: FileText, color: 'bg-orange-100 text-orange-600' },
];

export const KnowledgeBase: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [readingArticle, setReadingArticle] = useState<typeof ARTICLES[0] | null>(null);

    const filteredArticles = useMemo(() => {
        return ARTICLES.filter(b => {
            const matchesSearch = b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                b.content.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCategory = selectedCategory ? b.category === selectedCategory : true;
            return matchesSearch && matchesCategory;
        });
    }, [searchQuery, selectedCategory]);

    if (readingArticle) {
        return (
            <div className="bg-white rounded-xl shadow-lg border p-8 max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4">
                <button
                    onClick={() => setReadingArticle(null)}
                    className="mb-4 text-sm text-gray-500 hover:text-black flex items-center"
                >
                    <ChevronRight className="rotate-180 mr-1" size={16} /> Volver a la búsqueda
                </button>

                <div className="flex items-center gap-3 mb-6">
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-xs font-bold text-gray-600 uppercase">
                        {readingArticle.category}
                    </span>
                    <span className="text-xs text-gray-400">
                        {readingArticle.views} lecturas
                    </span>
                </div>

                <h1 className="text-3xl font-bold mb-6 text-gray-900">{readingArticle.title}</h1>

                <div className="prose max-w-none text-gray-700 leading-relaxed whitespace-pre-line">
                    {readingArticle.content}
                </div>

                <div className="mt-8 pt-8 border-t flex justify-between items-center bg-blue-50 p-4 rounded-lg">
                    <p className="text-blue-800 text-sm font-medium">¿No resolvió tu problema?</p>
                    <Button variant="primary" size="sm">Crear Ticket</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-8">
            {/* Header / Search */}
            <div className="text-center space-y-4 py-8">
                <h1 className="text-3xl font-bold text-gray-900">¿Cómo podemos ayudarte?</h1>
                <p className="text-gray-500">Busca soluciones rápidas antes de contactar a soporte</p>

                <div className="relative max-w-xl mx-auto">
                    <Search className="absolute left-4 top-3.5 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Buscar por palabras clave (ej: impresora, internet)..."
                        className="w-full pl-12 pr-4 py-3 rounded-full border border-gray-300 shadow-sm focus:ring-2 focus:ring-black focus:border-transparent outline-none transition"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* Categories */}
            {!searchQuery && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {CATEGORIES.map(cat => {
                        const Icon = cat.icon;
                        const isActive = selectedCategory === cat.id;
                        return (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(isActive ? null : cat.id)}
                                className={`p-6 rounded-xl border transition text-left flex flex-col items-center justify-center space-y-3
                                    ${isActive
                                        ? 'bg-black text-white border-black ring-2 ring-offset-2 ring-black'
                                        : 'bg-white hover:border-gray-400 hover:shadow-md'
                                    }`}
                            >
                                <div className={`p-3 rounded-full ${isActive ? 'bg-gray-800 text-white' : cat.color}`}>
                                    <Icon size={24} />
                                </div>
                                <span className="font-bold">{cat.name}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Results */}
            <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                    <Book size={20} className="mr-2" />
                    {searchQuery ? 'Resultados de búsqueda' : 'Artículos Populares'}
                </h2>

                <div className="bg-white rounded-xl shadow-sm border divide-y">
                    {filteredArticles.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            No encontramos artículos que coincidan con tu búsqueda.
                        </div>
                    ) : (
                        filteredArticles.map(article => (
                            <div
                                key={article.id}
                                onClick={() => setReadingArticle(article)}
                                className="p-4 hover:bg-gray-50 transition cursor-pointer flex justify-between items-center group"
                            >
                                <div>
                                    <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition">{article.title}</h3>
                                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">{article.content}</p>
                                    <span className="text-xs text-gray-400 mt-2 inline-block bg-gray-100 px-2 py-0.5 rounded">
                                        {article.category}
                                    </span>
                                </div>
                                <ChevronRight className="text-gray-300 group-hover:text-blue-600" />
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};
